import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { 
  ArrowLeft, Plus, Pencil, Trash2, Save, X, GripVertical,
  ChevronUp, ChevronDown, Eye, EyeOff
} from "lucide-react";
import { API } from "@/App";
import { toast } from "sonner";
import { useAdminAuth } from "@/context/AdminAuthContext";

const AdminCateringPage = () => {
  const { token } = useAdminAuth();
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingMenu, setEditingMenu] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    title_en: "",
    serves: "",
    serves_en: "",
    items: "",
    items_en: "",
    price: "",
    price_en: "",
    order_index: 0
  });

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchMenus();
  }, []);

  const fetchMenus = async () => {
    try {
      const response = await axios.get(`${API}/admin/catering-menus`, { headers });
      setMenus(response.data);
    } catch (error) {
      console.error("Error fetching catering menus:", error);
      toast.error("Kunne ikke hente cateringmenyer");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const payload = {
      title: formData.title,
      title_en: formData.title_en || null,
      serves: formData.serves,
      serves_en: formData.serves_en || null,
      items: formData.items.split("\n").filter(item => item.trim()),
      items_en: formData.items_en ? formData.items_en.split("\n").filter(item => item.trim()) : null,
      price: formData.price,
      price_en: formData.price_en || null,
      order_index: parseInt(formData.order_index) || 0
    };

    try {
      if (editingMenu) {
        await axios.put(`${API}/admin/catering-menus/${editingMenu.id}`, payload, { headers });
        toast.success("Cateringmeny oppdatert!");
      } else {
        await axios.post(`${API}/admin/catering-menus`, payload, { headers });
        toast.success("Cateringmeny opprettet!");
      }
      
      setShowForm(false);
      setEditingMenu(null);
      resetForm();
      fetchMenus();
    } catch (error) {
      console.error("Error saving catering menu:", error);
      toast.error("Kunne ikke lagre cateringmeny");
    }
  };

  const handleEdit = (menu) => {
    setEditingMenu(menu);
    setFormData({
      title: menu.title,
      title_en: menu.title_en || "",
      serves: menu.serves,
      serves_en: menu.serves_en || "",
      items: menu.items.join("\n"),
      items_en: menu.items_en ? menu.items_en.join("\n") : "",
      price: menu.price,
      price_en: menu.price_en || "",
      order_index: menu.order_index || 0
    });
    setShowForm(true);
  };

  const handleDelete = async (menuId) => {
    if (!window.confirm("Er du sikker på at du vil slette denne cateringmenyen?")) return;
    
    try {
      await axios.delete(`${API}/admin/catering-menus/${menuId}`, { headers });
      toast.success("Cateringmeny slettet!");
      fetchMenus();
    } catch (error) {
      console.error("Error deleting catering menu:", error);
      toast.error("Kunne ikke slette cateringmeny");
    }
  };

  const handleToggleActive = async (menu) => {
    try {
      await axios.put(`${API}/admin/catering-menus/${menu.id}`, 
        { is_active: !menu.is_active }, 
        { headers }
      );
      toast.success(menu.is_active ? "Meny deaktivert" : "Meny aktivert");
      fetchMenus();
    } catch (error) {
      toast.error("Kunne ikke oppdatere status");
    }
  };

  const handleMoveUp = async (menu, index) => {
    if (index === 0) return;
    const prevMenu = menus[index - 1];
    
    try {
      await axios.put(`${API}/admin/catering-menus/${menu.id}`, 
        { order_index: prevMenu.order_index }, 
        { headers }
      );
      await axios.put(`${API}/admin/catering-menus/${prevMenu.id}`, 
        { order_index: menu.order_index }, 
        { headers }
      );
      fetchMenus();
    } catch (error) {
      toast.error("Kunne ikke flytte meny");
    }
  };

  const handleMoveDown = async (menu, index) => {
    if (index === menus.length - 1) return;
    const nextMenu = menus[index + 1];
    
    try {
      await axios.put(`${API}/admin/catering-menus/${menu.id}`, 
        { order_index: nextMenu.order_index }, 
        { headers }
      );
      await axios.put(`${API}/admin/catering-menus/${nextMenu.id}`, 
        { order_index: menu.order_index }, 
        { headers }
      );
      fetchMenus();
    } catch (error) {
      toast.error("Kunne ikke flytte meny");
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      title_en: "",
      serves: "",
      serves_en: "",
      items: "",
      items_en: "",
      price: "",
      price_en: "",
      order_index: menus.length
    });
  };

  return (
    <div className="min-h-screen" style={{ background: '#f4e7d8' }}>
      {/* Header */}
      <div style={{ background: 'linear-gradient(135deg, #721111 0%, #2d1810 100%)', borderBottom: '3px solid #d4af37' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/panel050623" className="p-2 rounded-full hover:bg-white/10 transition-colors">
                <ArrowLeft className="h-6 w-6" style={{ color: '#d4af37' }} />
              </Link>
              <div>
                <h1 className="text-2xl font-serif-display font-bold" style={{ color: '#d4af37' }}>
                  Cateringmenyer
                </h1>
                <p className="text-sm" style={{ color: '#f5e6d3' }}>
                  Administrer cateringmenyer som vises på nettsiden
                </p>
              </div>
            </div>
            <Button
              onClick={() => { setShowForm(true); setEditingMenu(null); resetForm(); }}
              className="btn-gold rounded-full"
              data-testid="add-catering-menu-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Legg til Meny
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 overflow-y-auto">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-2xl max-w-2xl w-full p-6 shadow-2xl my-8"
              style={{ border: '3px solid #d4af37' }}
            >
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-serif-display font-bold" style={{ color: '#2d1810' }}>
                  {editingMenu ? "Rediger Cateringmeny" : "Ny Cateringmeny"}
                </h2>
                <button onClick={() => { setShowForm(false); setEditingMenu(null); }}>
                  <X className="h-6 w-6" style={{ color: '#721111' }} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label style={{ color: '#2d1810' }}>Tittel (Norsk) *</Label>
                    <Input
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      placeholder="F.eks. Bryllupsfest"
                      required
                      className="indian-input"
                    />
                  </div>
                  <div>
                    <Label style={{ color: '#2d1810' }}>Title (English)</Label>
                    <Input
                      value={formData.title_en}
                      onChange={(e) => setFormData({ ...formData, title_en: e.target.value })}
                      placeholder="E.g. Wedding Party"
                      className="indian-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label style={{ color: '#2d1810' }}>Antall gjester (Norsk) *</Label>
                    <Input
                      value={formData.serves}
                      onChange={(e) => setFormData({ ...formData, serves: e.target.value })}
                      placeholder="F.eks. 50-200 gjester"
                      required
                      className="indian-input"
                    />
                  </div>
                  <div>
                    <Label style={{ color: '#2d1810' }}>Guests (English)</Label>
                    <Input
                      value={formData.serves_en}
                      onChange={(e) => setFormData({ ...formData, serves_en: e.target.value })}
                      placeholder="E.g. 50-200 guests"
                      className="indian-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label style={{ color: '#2d1810' }}>Pris (Norsk) *</Label>
                    <Input
                      value={formData.price}
                      onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                      placeholder="F.eks. kr 495,- per person"
                      required
                      className="indian-input"
                    />
                  </div>
                  <div>
                    <Label style={{ color: '#2d1810' }}>Price (English)</Label>
                    <Input
                      value={formData.price_en}
                      onChange={(e) => setFormData({ ...formData, price_en: e.target.value })}
                      placeholder="E.g. NOK 495 per person"
                      className="indian-input"
                    />
                  </div>
                </div>

                <div>
                  <Label style={{ color: '#2d1810' }}>Retter (Norsk) * - én per linje</Label>
                  <Textarea
                    value={formData.items}
                    onChange={(e) => setFormData({ ...formData, items: e.target.value })}
                    placeholder="Butter Chicken&#10;Palak Paneer&#10;Naan Brød&#10;..."
                    rows={6}
                    required
                    className="indian-input"
                  />
                </div>

                <div>
                  <Label style={{ color: '#2d1810' }}>Dishes (English) - one per line</Label>
                  <Textarea
                    value={formData.items_en}
                    onChange={(e) => setFormData({ ...formData, items_en: e.target.value })}
                    placeholder="Butter Chicken&#10;Spinach Paneer&#10;Naan Bread&#10;..."
                    rows={6}
                    className="indian-input"
                  />
                </div>

                <div>
                  <Label style={{ color: '#2d1810' }}>Rekkefølge (lavere = først)</Label>
                  <Input
                    type="number"
                    value={formData.order_index}
                    onChange={(e) => setFormData({ ...formData, order_index: e.target.value })}
                    className="indian-input w-24"
                  />
                </div>

                <div className="flex gap-3 pt-4" style={{ borderTop: '2px solid #d4af37' }}>
                  <Button
                    type="button"
                    onClick={() => { setShowForm(false); setEditingMenu(null); }}
                    variant="outline"
                    className="flex-1 rounded-full"
                    style={{ borderColor: '#b27f47', color: '#2d1810' }}
                  >
                    Avbryt
                  </Button>
                  <Button type="submit" className="flex-1 rounded-full btn-maroon">
                    <Save className="h-4 w-4 mr-2" />
                    {editingMenu ? "Oppdater" : "Lagre"}
                  </Button>
                </div>
              </form>
            </motion.div>
          </div>
        )}

        {/* Menu List */}
        {loading ? (
          <p className="text-center py-12" style={{ color: '#4a3728' }}>Laster...</p>
        ) : menus.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl" style={{ border: '2px solid #d4af37' }}>
            <p style={{ color: '#4a3728' }}>Ingen cateringmenyer ennå.</p>
            <p className="text-sm mt-2" style={{ color: '#8b4513' }}>
              Klikk "Legg til Meny" for å opprette din første cateringmeny.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {menus.map((menu, index) => (
              <motion.div
                key={menu.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`bg-white rounded-xl p-6 shadow-md ${!menu.is_active ? 'opacity-60' : ''}`}
                style={{ border: menu.is_active ? '2px solid #d4af37' : '2px solid #ccc' }}
                data-testid={`catering-menu-${menu.id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-xl font-serif-display font-bold" style={{ color: '#2d1810' }}>
                        {menu.title}
                      </h3>
                      {!menu.is_active && (
                        <span className="px-2 py-0.5 rounded-full text-xs bg-gray-200 text-gray-600">
                          Deaktivert
                        </span>
                      )}
                    </div>
                    <p className="text-sm mb-2" style={{ color: '#8b4513' }}>{menu.serves}</p>
                    <div className="flex flex-wrap gap-2 mb-3">
                      {menu.items.slice(0, 5).map((item, i) => (
                        <span key={i} className="px-2 py-1 rounded-full text-xs" style={{ background: '#f4e7d8', color: '#4a3728' }}>
                          {item}
                        </span>
                      ))}
                      {menu.items.length > 5 && (
                        <span className="px-2 py-1 rounded-full text-xs" style={{ background: '#d4af37', color: '#2d1810' }}>
                          +{menu.items.length - 5} mer
                        </span>
                      )}
                    </div>
                    <p className="text-lg font-bold" style={{ color: '#721111' }}>{menu.price}</p>
                  </div>
                  
                  <div className="flex items-center gap-2 ml-4">
                    <div className="flex flex-col gap-1">
                      <button
                        onClick={() => handleMoveUp(menu, index)}
                        disabled={index === 0}
                        className="p-1 rounded hover:bg-gray-100 disabled:opacity-30"
                        title="Flytt opp"
                      >
                        <ChevronUp className="h-4 w-4" style={{ color: '#721111' }} />
                      </button>
                      <button
                        onClick={() => handleMoveDown(menu, index)}
                        disabled={index === menus.length - 1}
                        className="p-1 rounded hover:bg-gray-100 disabled:opacity-30"
                        title="Flytt ned"
                      >
                        <ChevronDown className="h-4 w-4" style={{ color: '#721111' }} />
                      </button>
                    </div>
                    <button
                      onClick={() => handleToggleActive(menu)}
                      className="p-2 rounded-lg hover:bg-gray-100"
                      title={menu.is_active ? "Deaktiver" : "Aktiver"}
                    >
                      {menu.is_active ? (
                        <Eye className="h-5 w-5" style={{ color: '#22c55e' }} />
                      ) : (
                        <EyeOff className="h-5 w-5" style={{ color: '#9ca3af' }} />
                      )}
                    </button>
                    <button
                      onClick={() => handleEdit(menu)}
                      className="p-2 rounded-lg hover:bg-gray-100"
                      title="Rediger"
                    >
                      <Pencil className="h-5 w-5" style={{ color: '#d4af37' }} />
                    </button>
                    <button
                      onClick={() => handleDelete(menu.id)}
                      className="p-2 rounded-lg hover:bg-red-50"
                      title="Slett"
                    >
                      <Trash2 className="h-5 w-5" style={{ color: '#ef4444' }} />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminCateringPage;
